import { useMutation } from "@apollo/client/react";
import Nango, { AuthError } from "@nangohq/frontend";
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { MailIcon } from "lucide-react";
import { GoogleIcon, MicrosoftIcon } from "@/objects/@icons";

import {
  EmailConnectionStatusEnum,
  EmailConnectionVisibilityEnum,
  ExternalProviderEnum,
  type EmailConnectionsQuery,
} from "@/__generated__/graphql";
import { BoxForEntity } from "@/components/atoms/BoxForEntity";
import { Icon } from "@/components/atoms/Icon";
import { navigableCardSelectionClassName } from "@/components/molecules/NavigableCardGrid";
import { StickyLayout } from "@/components/organisms/StickyLayout";
import { Mutations, Queries } from "@/constants";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { useContextTree } from "@/hooks/useContextTree";
import { State } from "@/state";
import type { CommandContextType } from "@/state/@types";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export const COMMAND_ID = "connect-mailbox";

export type ConnectMailboxContextType = {
  /**
   * The business it was asked for from: the one it is to be searched for to
   * begin with, beside the person's own books.
   */
  entityIdentifier?: string;
  /**
   * A mailbox of theirs its provider signed out, to sign in to again: the
   * same mailbox, given where it was.
   */
  reconnectIdentifier?: string;
};

/** A mailbox a person has connected, and the businesses it is given to. */
export type Mailbox = NonNullable<
  EmailConnectionsQuery["emailConnections"]["data"]
>[number];

/**
 * The mailbox drawn while the mailboxes load: a typical one - the viewer's
 * own, given to a business - drawn as any other, each piece wrapped.
 */
export const PLACEHOLDER_MAILBOX: Mailbox = {
  __typename: "EmailConnectionObject",
  id: "placeholder",
  identifier: "placeholder",
  provider: ExternalProviderEnum.Gmail,
  emailAddress: "someone@business.example.com",
  status: EmailConnectionStatusEnum.Connected,
  lastSearchedAt: null,
  ownedByViewer: true,
  ownerName: "",
  assignments: [
    {
      __typename: "EmailConnectionAssignmentObject",
      id: "placeholder",
      identifier: "placeholder",
      visibility: EmailConnectionVisibilityEnum.Private,
      entity: {
        __typename: "EntityObject",
        id: "placeholder",
        identifier: "placeholder",
        name: "Business",
        isUserEntity: false,
      },
    },
  ],
};

/** The mailboxes a person can connect, by their provider's key at Nango. */
export const MAILBOX_PROVIDERS = [
  {
    providerConfigKey: "google-mail",
    provider: ExternalProviderEnum.Gmail,
    title: "Gmail",
    description: "A Google mailbox, or Google Workspace.",
    ReactIcon: GoogleIcon,
  },
  {
    providerConfigKey: "outlook",
    provider: ExternalProviderEnum.Outlook,
    title: "Outlook",
    description: "An Outlook or Microsoft 365 mailbox.",
    ReactIcon: MicrosoftIcon,
  },
] as const;

/** How long a sign-in session is used for: Nango's last thirty minutes. */
const SESSION_LIFETIME_MS = 25 * 60 * 1000;

/**
 * Connecting one of the person's mailboxes: Nango's sign-in window for its
 * provider, then the mailbox recorded as theirs, in their own books - and
 * given to the businesses named too, so it is searched for each at once. One
 * already connected is connected again, given where it was. How it went is
 * said in the snackbar; it resolves to whether the mailbox was connected.
 *
 * The window opens in the click itself - Safari blocks one opened after
 * anything the click waited on - so each provider's sign-in session is asked
 * of Nango ahead: as the hook is first used, again once one is spent, and
 * again before Nango's thirty minutes are up. So is the one that signs in
 * again a mailbox its provider signed out (*reconnecting*).
 */
export function useConnectMailbox(
  reconnecting: { identifier: string; providerConfigKey: string } | null = null,
) {
  const [connectingProvider, setConnectingProvider] = useState<string | null>(
    null,
  );
  const [createConnectSession] = useMutation(
    Mutations.CREATE_EMAIL_CONNECT_SESSION,
  );
  const [connectEmailAccount] = useMutation(Mutations.CONNECT_EMAIL_ACCOUNT, {
    refetchQueries: ["EmailConnections"],
  });
  const [assignEmailAccount] = useMutation(Mutations.ASSIGN_EMAIL_ACCOUNT, {
    refetchQueries: ["EmailConnections"],
  });

  const sessionsRef = useRef<Record<string, { token: string; at: number }>>({});
  // By provider, and the one to sign a mailbox in again by that mailbox.
  const prepare = useCallback(
    async (providerConfigKey: string, connectionIdentifier?: string) => {
      const session = await createConnectSession({
        variables: { input: { providerConfigKey, connectionIdentifier } },
      });
      const payload = session.data?.createEmailConnectSession;
      if (payload?.data) {
        sessionsRef.current[connectionIdentifier ?? providerConfigKey] = {
          token: payload.data,
          at: Date.now(),
        };
      }
      return payload;
    },
    [createConnectSession],
  );
  useEffect(() => {
    // One not had now is asked for again as it is clicked.
    const prepareEach = () => {
      for (const { providerConfigKey } of MAILBOX_PROVIDERS) {
        prepare(providerConfigKey).catch(() => {});
      }
    };
    prepareEach();
    const renew = window.setInterval(prepareEach, SESSION_LIFETIME_MS);
    return () => window.clearInterval(renew);
  }, [prepare]);
  const reconnectingIdentifier = reconnecting?.identifier;
  const reconnectingKey = reconnecting?.providerConfigKey;
  useEffect(() => {
    if (!reconnectingIdentifier || !reconnectingKey) return;
    const prepareIt = () =>
      prepare(reconnectingKey, reconnectingIdentifier).catch(() => {});
    prepareIt();
    const renew = window.setInterval(prepareIt, SESSION_LIFETIME_MS);
    return () => window.clearInterval(renew);
  }, [prepare, reconnectingIdentifier, reconnectingKey]);

  const connect = useCallback(
    async (
      providerConfigKey: string,
      {
        entityIdentifiers = [],
        connected = [],
        reconnectIdentifier,
      }: {
        /** The businesses to search it for, beside its owner's books. */
        entityIdentifiers?: string[];
        /** The person's mailboxes already connected: signed in to again, one is reconnected. */
        connected?: string[];
        /** A mailbox of theirs its provider signed out: signed in to again as itself. */
        reconnectIdentifier?: string;
      } = {},
    ) => {
      setConnectingProvider(providerConfigKey);
      try {
        // Nothing awaited before the window opens, while there is a session
        // ready: Safari would block it.
        const ready =
          sessionsRef.current[reconnectIdentifier ?? providerConfigKey];
        let token =
          ready && Date.now() - ready.at < SESSION_LIFETIME_MS
            ? ready.token
            : null;
        if (!token) {
          // None ready: asked for now, and a browser that blocks the window
          // for the wait says so - the next click has one.
          const payload = await prepare(providerConfigKey, reconnectIdentifier);
          token = payload?.data ?? null;
          if (!token) {
            State.getState().setSnackbarMessage(
              payload?.errors?.genericErrors?.[0]?.message ??
                "Couldn't start connecting the mailbox. Try again.",
            );
            return false;
          }
        }

        const nango = new Nango({ connectSessionToken: token });
        const result = await nango.auth(providerConfigKey);
        const saved = await connectEmailAccount({
          variables: {
            input: {
              nangoConnectionId: result.connectionId,
              providerConfigKey: result.providerConfigKey,
            },
          },
        });
        const mailbox = saved.data?.connectEmailAccount?.data;
        if (!mailbox) {
          State.getState().setSnackbarMessage(
            saved.data?.connectEmailAccount?.errors?.genericErrors?.[0]
              ?.message ?? "Couldn't connect the mailbox. Try again.",
          );
          return false;
        }

        // Given to the businesses picked for it, beside wherever it is already
        // given - its owner's books, at the least.
        const given = mailbox.assignments.map(
          (assignment) => assignment.entity.identifier,
        );
        const businesses = State.getState()
          .entities.filter(
            (entity) =>
              entityIdentifiers.includes(entity.identifier) &&
              !entity.isUserEntity,
          )
          .map((entity) => entity.name);
        const named =
          businesses.length > 1
            ? `${businesses.slice(0, -1).join(", ")} and ${businesses[businesses.length - 1]}`
            : businesses[0];
        const verb = connected.includes(mailbox.identifier)
          ? "reconnected"
          : "connected";
        const added = entityIdentifiers.filter(
          (identifier) => !given.includes(identifier),
        );
        if (added.length > 0) {
          const assigned = await assignEmailAccount({
            variables: {
              input: {
                identifier: mailbox.identifier,
                entityIdentifiers: [...given, ...added],
              },
            },
          });
          if (!assigned.data?.assignEmailAccount?.data) {
            State.getState().setSnackbarMessage(
              `${mailbox.emailAddress} is ${verb}, but couldn't be given to ${named}. Give it there in Settings, under Emails.`,
            );
            return true;
          }
        }
        State.getState().setSnackbarMessage(
          named
            ? `${mailbox.emailAddress} is ${verb}, and searched for ${named}.`
            : verb === "reconnected"
              ? `${mailbox.emailAddress} is reconnected.`
              : "Mailbox connected to your personal books. Give it to any of your businesses in Settings, under Emails.",
        );
        return true;
      } catch (error) {
        const message =
          error instanceof AuthError && error.type === "window_closed"
            ? "The sign-in window closed before you finished. Try again."
            : error instanceof AuthError && error.type === "blocked_by_browser"
              ? "Your browser blocked the sign-in window. Click again to open it."
              : `Couldn't connect the mailbox: ${error instanceof Error ? error.message : "unknown error"}`;
        State.getState().setSnackbarMessage(message);
        return false;
      } finally {
        setConnectingProvider(null);
        // Spent, or used up by a window that never opened: another, ready.
        prepare(providerConfigKey, reconnectIdentifier).catch(() => {});
      }
    },
    [assignEmailAccount, connectEmailAccount, prepare],
  );

  return { connect, connectingProvider };
}

/**
 * Which businesses it is to be searched for - the one it was opened for to
 * begin with, the person's own books always - and which mailbox to connect,
 * picked by the keyboard or a click: its sign-in opens over wherever the
 * person is, and once it is connected they are back where they were.
 */
function Component({ context }: { context?: CommandContextType }) {
  // Typed for every command's context, as the dispatcher does not know whose
  // it is: this one's, as it was pushed.
  const { entityIdentifier, reconnectIdentifier } =
    (context as ConnectMailboxContextType | undefined) ?? {};
  const entities = State((state) => state.entities);
  const personal = entities.find((entity) => entity.isUserEntity);
  const businesses = entities.filter((entity) => !entity.isUserEntity);
  // The mailboxes already searched for each business: whether another is
  // wanted there, and which are signed in to again rather than anew.
  const mailboxes = useBackendQuery(Queries.EMAIL_CONNECTIONS_QUERY, {
    variables: {
      entityIdentifiers: entities.map((entity) => entity.identifier),
    },
    fetchPolicy: "cache-and-network",
    skip: entities.length === 0,
  });
  const isLoadingMailboxes = mailboxes.loading && !mailboxes.data;
  const known = mailboxes.data?.emailConnections?.data ?? [];
  // The one to sign in to again, by its provider's sign-in.
  const signedOut = known.find(
    (mailbox) => mailbox.identifier === reconnectIdentifier,
  );
  const reconnectingKey =
    MAILBOX_PROVIDERS.find(
      (provider) => provider.provider === signedOut?.provider,
    )?.providerConfigKey ?? null;
  const { connect, connectingProvider } = useConnectMailbox(
    signedOut && reconnectingKey
      ? { identifier: signedOut.identifier, providerConfigKey: reconnectingKey }
      : null,
  );
  const searchedFor = (identifier: string): Mailbox[] =>
    isLoadingMailboxes
      ? [PLACEHOLDER_MAILBOX]
      : known.filter((mailbox) =>
          mailbox.assignments.some(
            (assignment) => assignment.entity.identifier === identifier,
          ),
        );
  const [picked, setPicked] = useState<string[]>(() =>
    businesses.some((entity) => entity.identifier === entityIdentifier)
      ? [entityIdentifier!]
      : [],
  );
  // Where the keyboard is: a business's row, or a mailbox's card.
  const [focused, setFocused] = useState<string | null>(null);

  // One grid for the keyboard: a row for each business there is to tick,
  // then the mailboxes side by side under them.
  const {
    Scope,
    ref: scopeRef,
    getCellProps,
    getCell,
  } = useContextTree({
    cellNavigation: {
      rowCount: businesses.length + 1,
      colCount: (rowIndex) =>
        rowIndex < businesses.length ? 1 : MAILBOX_PROVIDERS.length,
      wrap: false,
    },
  });
  // The business it was asked for from ticked already, it starts on the
  // first mailbox: Enter signs in to it.
  useEffect(() => {
    requestAnimationFrame(() => getCell(businesses.length, 0)?.focus());
  }, [businesses.length, getCell]);

  const toggle = (identifier: string) => {
    if (connectingProvider !== null) return;
    setPicked((current) =>
      current.includes(identifier)
        ? current.filter((one) => one !== identifier)
        : [...current, identifier],
    );
  };
  const signIn = (providerConfigKey: string) => {
    if (connectingProvider !== null) return;
    void connect(providerConfigKey, {
      entityIdentifiers: picked,
      connected: known
        .filter((mailbox) => mailbox.ownedByViewer)
        .map((mailbox) => mailbox.identifier),
      reconnectIdentifier:
        signedOut && providerConfigKey === reconnectingKey
          ? signedOut.identifier
          : undefined,
    }).then((connected) => {
      if (connected) State.getState().popCommandsFromStack();
    });
  };
  // A stop's Enter and Space do what its click does.
  const pressed = (act: () => void) => (event: KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      act();
    }
  };
  const stop = (key: string) => ({
    onFocus: () => setFocused(key),
    onBlur: () => setFocused(null),
  });

  return (
    <StickyLayout>
      <Scope
        ref={scopeRef}
        tabIndex={-1}
        style={{ display: "block", outline: "none" }}
      >
        <div className="flex flex-col gap-1">
          <Typography
            level="body-sm"
            className="pl-4 pr-4 pt-4 text-muted-foreground"
          >
            {signedOut
              ? `Sign in to ${signedOut.emailAddress} again: its provider signed Neoledger out of it. It stays searched for each business it was.`
              : "Sign in to a mailbox and it is searched for the receipts of each business ticked. It is yours alone in each until you share it there."}
          </Typography>
          <div
            data-testid="connect-mailbox-businesses"
            className="pl-4 pr-4 pt-2 flex flex-col gap-0.5"
          >
            <Typography level="title-sm" className="mb-1">
              Search it for
            </Typography>
            {[...(personal ? [personal] : []), ...businesses].map((entity) => {
              const isPersonal = entity.isUserEntity;
              const isTicked = isPersonal || picked.includes(entity.identifier);
              return (
                <div
                  key={entity.identifier}
                  data-testid="connect-mailbox-business"
                  // Its owner's books it is always in; each business a stop,
                  // theirs to tick until a sign-in is under way.
                  {...(isPersonal
                    ? {}
                    : {
                        ...getCellProps({
                          rowIndex: businesses.indexOf(entity),
                          colIndex: 0,
                        }),
                        ...stop(entity.identifier),
                        "data-business": entity.identifier,
                        tabIndex: 0,
                        role: "checkbox",
                        "aria-checked": isTicked,
                        onClick: () => toggle(entity.identifier),
                        onKeyDown: pressed(() => toggle(entity.identifier)),
                      })}
                  className={cn(
                    "flex items-center gap-2 -ml-2 -mr-2 pl-2 pr-2 pt-1 pb-1 rounded-md [outline:none]",
                    isPersonal ? "cursor-default" : "cursor-pointer",
                    focused === entity.identifier && "bg-muted",
                    isPersonal ? "" : "hover:bg-muted",
                  )}
                >
                  {/* Drawn, not pressed: the row is the stop. */}
                  <Checkbox
                    checked={isTicked}
                    disabled={isPersonal || connectingProvider !== null}
                    className="pointer-events-none"
                    tabIndex={-1}
                  />
                  <BoxForEntity identifier={entity.identifier} size="sm" />
                  <Typography
                    level="body-sm"
                    data-testid="connect-mailbox-business-name"
                    className={cn(
                      "truncate",
                      "min-w-0 shrink-0",
                      isPersonal && "text-muted-foreground/80",
                    )}
                  >
                    {isPersonal ? "Your personal books" : entity.name}
                  </Typography>
                  {/* At its end, the mailboxes already searched for it: two,
                      and how many more, or that there is none yet. */}
                  <div
                    data-testid="connect-mailbox-business-mailboxes"
                    className="ml-auto min-w-0 flex items-center justify-end gap-1"
                  >
                    {(() => {
                      const given = searchedFor(entity.identifier);
                      if (given.length === 0) {
                        return (
                          <Typography
                            level="body-xs"
                            className="text-muted-foreground/80"
                          >
                            No mailbox yet
                          </Typography>
                        );
                      }
                      return (
                        <>
                          {given.slice(0, 2).map((mailbox) => (
                            <Skeleton
                              key={mailbox.identifier}
                              loading={isLoadingMailboxes}
                              variant="rectangular"
                              className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
                            >
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge
                                    variant="secondary"
                                    data-testid="connect-mailbox-business-mailbox"
                                    className="min-w-0 max-w-55"
                                  >
                                    <Icon
                                      ReactIcon={
                                        mailbox.provider ===
                                        ExternalProviderEnum.Outlook
                                          ? MicrosoftIcon
                                          : GoogleIcon
                                      }
                                      size="xs"
                                    />
                                    {mailbox.emailAddress}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                  {mailbox.ownedByViewer
                                    ? "Yours"
                                    : `${mailbox.ownerName}'s, shared with you`}
                                </TooltipContent>
                              </Tooltip>
                            </Skeleton>
                          ))}
                          {given.length > 2 ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge
                                  variant="secondary"
                                  data-testid="connect-mailbox-business-mailboxes-more"
                                >
                                  +{given.length - 2}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                {given
                                  .slice(2)
                                  .map((mailbox) =>
                                    mailbox.ownedByViewer
                                      ? mailbox.emailAddress
                                      : `${mailbox.emailAddress}, ${mailbox.ownerName}'s, shared with you`,
                                  )
                                  .join(" · ")}
                              </TooltipContent>
                            </Tooltip>
                          ) : null}
                        </>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-4 p-4">
            {MAILBOX_PROVIDERS.map((provider, index) => {
              const isConnecting =
                connectingProvider === provider.providerConfigKey;
              const isDisabled = connectingProvider !== null && !isConnecting;
              const cellProps = getCellProps({
                rowIndex: businesses.length,
                colIndex: index,
              });
              return (
                <Card
                  key={provider.providerConfigKey}
                  {...cellProps}
                  {...stop(provider.providerConfigKey)}
                  data-testid="connect-mailbox-provider"
                  data-provider={provider.providerConfigKey}
                  tabIndex={isDisabled ? -1 : 0}
                  aria-disabled={isDisabled}
                  onClick={() => signIn(provider.providerConfigKey)}
                  onKeyDown={pressed(() => signIn(provider.providerConfigKey))}
                  className={cn(
                    cellProps.className,
                    isDisabled ? "cursor-default opacity-50" : "cursor-pointer",
                    "h-full",
                    navigableCardSelectionClassName({
                      isActive: focused === provider.providerConfigKey,
                      isFocusWithin: focused !== null,
                    }),
                  )}
                >
                  <CardHeader>
                    <Icon ReactIcon={provider.ReactIcon} size="lg" />
                    <CardTitle>{provider.title}</CardTitle>
                    <CardDescription>
                      {isConnecting
                        ? "Finish signing in, in the window that opened."
                        : provider.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </Scope>
    </StickyLayout>
  );
}

export function ConnectMailboxCommand() {
  const registerCommandDefinition = State(
    (state) => state.registerCommandDefinition,
  );
  const unregisterCommandDefinition = State(
    (state) => state.unregisterCommandDefinition,
  );

  useLayoutEffect(() => {
    registerCommandDefinition({
      id: COMMAND_ID,
      title: "Connect a mailbox",
      description: "Gmail or Outlook, searched for receipts…",
      getLabel: () => "Connect a mailbox",
      component: Component,
      ReactIcon: MailIcon,
      group: "Integrations",
    });
    return () => {
      unregisterCommandDefinition(COMMAND_ID);
    };
  }, []);

  return null;
}
